// Rapor Ayarları formu — SAF dönüşümler (spec 2026-10-16 S5; plan netleşen 13–15). Üstte "Raporda Hangi Kırılımlar Olsun"
// kutucukları, altta kapalı "Gelişmiş" eşikler. Ekranda OLMAYAN ayarlar (birim grupları, ısı haritası satırları) kayıtlı
// değerleriyle KORUNUR; kayıtlı "birim grubu" kırılımı listede kalır ve "Birim" işaretlenince düşer (ikisi birlikte yasak —
// final inceleme C2). Eski "Anket Yapısı" düzenleyicisinin boyut/soru dönüşümleri kaldırıldı: yapı yayında kendiliğinden
// kurulur (SurveyAutoStructure).
//
// Public anket: İK kırılımları (birim, lokasyon, kıdem, yönetici durumu, birim grubu) ve birim grupları geçersizdir
// (sunucu doğrulayıcısı reddeder). İç olarak yayınlanıp sonradan public'e çevrilen ankette kayıtlı ayarda kalmış olabilirler;
// çekmece onları göstermez, forma almaz ve kayıtta düşürür (yürütücü kararı, Görev 9).

export const MAX_BREAKDOWNS = 6;
export const STANDARD_BREAKDOWNS = ['department', 'location', 'tenure', 'managerStatus'];

// Literal anahtarlar — statik anahtar tarayıcıları görsün.
const BREAKDOWN_LABEL_KEYS = {
  department: 'survey.report.breakdown.department',
  location: 'survey.report.breakdown.location',
  tenure: 'survey.report.breakdown.tenure',
  managerStatus: 'survey.report.breakdown.managerStatus',
};

const str = (v) => (v === null || v === undefined ? '' : String(v));

export function parseIntList(text) {
  const parts = str(text).split(',').map((p) => p.trim()).filter(Boolean);
  const nums = parts.map(Number);
  return nums.every((n) => Number.isInteger(n)) ? nums : null;
}

/** Public ankette yalnız soru kırılımı geçerlidir. */
const allowedFor = (isPublic) => (b) => !isPublic || b.kind === 'question';

export function settingsToForm(settings, isPublic = false) {
  const s = settings || {};
  return {
    favorableMin: str(s.favorableMin ?? 4),
    unfavorableMax: str(s.unfavorableMax ?? 2),
    strongMin: str(s.summaryBands?.strongMin ?? 70),
    watchMin: str(s.summaryBands?.watchMin ?? 50),
    heatmapBands: (s.heatmapBands || [75, 65, 50]).join(', '),
    tenureBands: (s.tenureBandYears || [2, 10]).join(', '),
    breakdowns: (s.breakdowns || [])
      .filter(allowedFor(isPublic))
      .map((b) => ({ kind: b.kind, questionId: b.kind === 'question' ? b.questionId || null : null })),
  };
}

const same = (a, b) => a.kind === b.kind && (a.kind !== 'question' || a.questionId === b.questionId);

/** Ekrandaki kutucuklar: iç ankette dört standart kırılım + kırılım soruları; public ankette yalnız kırılım soruları. */
export function breakdownChoices(t, isPublic, questions) {
  const standard = isPublic
    ? []
    : STANDARD_BREAKDOWNS.map((kind) => ({ kind, questionId: null, label: t(BREAKDOWN_LABEL_KEYS[kind]) }));
  const asked = (questions || [])
    .filter((q) => q.isBreakdown && q.questionType === 'singleChoice')
    .map((q) => ({ kind: 'question', questionId: q.id, label: q.questionText }));
  return [...standard, ...asked];
}

export const isBreakdownChecked = (form, choice) => form.breakdowns.some((b) => same(b, choice));

export function toggleBreakdown(form, choice, checked) {
  const rest = form.breakdowns.filter((b) => !same(b, choice));
  if (!checked) return { ...form, breakdowns: rest };
  if (isBreakdownChecked(form, choice)) return form;
  const next = choice.kind === 'department' ? rest.filter((b) => b.kind !== 'departmentGroup') : rest;
  if (next.length >= MAX_BREAKDOWNS) return form;
  return {
    ...form,
    breakdowns: [...next, { kind: choice.kind, questionId: choice.kind === 'question' ? choice.questionId : null }],
  };
}

const toInt = (v) => {
  if (str(v).trim() === '') return null;
  const n = Number(v);
  return Number.isInteger(n) ? n : null;
};

/**
 * Form → SurveyReportSettings. `original`: kayıtlı ayar (ekranda olmayan alanlar ondan korunur).
 * `choices`: breakdownChoices çıktısı (kutucuğu olmayan soru kırılımı düşer — sunucu zaten reddederdi).
 * `isPublic`: public ankette İK kırılımları ve birim grupları düşer (sunucu doğrulayıcısı reddederdi).
 */
export function formToSettings(form, original, choices, isPublic = false) {
  const favorableMin = toInt(form.favorableMin);
  const unfavorableMax = toInt(form.unfavorableMax);
  if (favorableMin === null || unfavorableMax === null) return { error: 'survey.report.invalidSettings.scale' };
  const strongMin = toInt(form.strongMin);
  const watchMin = toInt(form.watchMin);
  if (strongMin === null || watchMin === null) return { error: 'survey.report.invalidSettings.summaryBands' };
  const heatmapBands = parseIntList(form.heatmapBands);
  if (!heatmapBands) return { error: 'survey.report.invalidSettings.heatmapBands' };
  const tenureBandYears = parseIntList(form.tenureBands);
  if (!tenureBandYears) return { error: 'survey.report.invalidSettings.tenureBands' };

  const knownQuestions = new Set((choices || []).filter((c) => c.kind === 'question').map((c) => c.questionId));
  const breakdowns = form.breakdowns
    .filter(allowedFor(isPublic))
    .filter((b) => b.kind !== 'question' || knownQuestions.has(b.questionId));
  const kinds = new Set(breakdowns.map((b) => b.kind));
  if (kinds.has('department') && kinds.has('departmentGroup')) {
    return { error: 'survey.report.invalidSettings.departmentAndGroup' };
  }

  const o = original || {};
  return {
    value: {
      favorableMin,
      unfavorableMax,
      summaryBands: { strongMin, watchMin },
      heatmapBands,
      tenureBandYears,
      breakdowns: breakdowns.map((b) => ({ kind: b.kind, questionId: b.kind === 'question' ? b.questionId : null })),
      // Ekranda yok (spec S5) — kayıtlı değer aynen geri yazılır (public ankette geçersiz → boş).
      departmentGroups: isPublic
        ? []
        : (o.departmentGroups || []).map((g) => ({ name: g.name, departmentIds: [...(g.departmentIds || [])] })),
      heatmapSubDimensionIds: Array.isArray(o.heatmapSubDimensionIds) && o.heatmapSubDimensionIds.length
        ? [...o.heatmapSubDimensionIds] : null,
    },
  };
}
